import React from 'react';
import { Heading, Link, LogoLink, NavLink, RichText } from '@/components';
import type { PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { LinkInterface } from '@/types';

interface FooterProps {
  navigationLinks: LinkInterface[];
  copyrightText: string;
  businessHours?: PortableTextBlockType;
  locationInfo?: PortableTextBlockType;
  newsletterUrl: string;
  instagramLink?: string;
  facebookLink?: string;
  twitterLink?: string;
  youtubeLink?: string;
}

const Footer: React.FC<FooterProps> = ({
  navigationLinks,
  copyrightText,
  businessHours,
  locationInfo,
  newsletterUrl,
  instagramLink,
  facebookLink,
  twitterLink,
  youtubeLink,
}) => {
  return (
    <footer className='bg-blue-800 font-light'>
      <div className='relative'>
        <div className='relative z-10 mx-auto flex max-w-screen-xl flex-col md:flex-row'>
          <div className='w-full bg-green-500 px-5 py-8 text-center md:w-5/12 md:p-[50px] xl:w-[460px]'>
            <div>
              <img
                src='/images/mail.svg'
                className='mx-auto max-h-6'
                alt='Email Icon'
              />
              <h2 className='mb-0.5 mt-1 text-4xl font-bold text-black'>stay in the loop</h2>
              <p className='m-0 text-xl font-normal text-black'>
                sign up to receive our newsletter.
              </p>
            </div>
            <div className='mt-3'>
              <Link
                externalLink={newsletterUrl}
                className='w-full'
                variant='default'
                size='sm'
                ariaLabel='Join our newsletter'
              >
                join our newsletter
              </Link>
            </div>
            <div className='mt-4 text-center'>
              <ul className='flex justify-center gap-x-5'>
                {instagramLink && (
                  <li>
                    <Link
                      externalLink={instagramLink}
                      variant='icon'
                      size='icon'
                      className='bg-white hover:bg-white focus:bg-white active:bg-white'
                      ariaLabel='Instagram'
                    >
                      <img
                        src='/images/instagram-logo.svg'
                        alt='Instagram Logo'
                        className='h-5 w-5 rounded-full'
                      />
                    </Link>
                  </li>
                )}
                {facebookLink && (
                  <li>
                    <Link
                      externalLink={facebookLink}
                      variant='icon'
                      size='icon'
                      className='bg-white hover:bg-white focus:bg-white active:bg-white'
                      ariaLabel='Facebook'
                    >
                      <img
                        src='/images/facebook-logo.svg'
                        alt='Facebook Logo'
                        className='h-5 w-5 rounded-full'
                      />
                    </Link>
                  </li>
                )}
                {twitterLink && (
                  <li>
                    <Link
                      externalLink={twitterLink}
                      variant='icon'
                      size='icon'
                      className='bg-white hover:bg-white focus:bg-white active:bg-white'
                      ariaLabel='Twitter'
                    >
                      <img
                        src='/images/twitter-logo.svg'
                        alt='Twitter Logo'
                        className='h-5 w-5 rounded-full'
                      />
                    </Link>
                  </li>
                )}
                {youtubeLink && (
                  <li>
                    <Link
                      externalLink={youtubeLink}
                      variant='icon'
                      size='icon'
                      className='bg-white hover:bg-white focus:bg-white active:bg-white'
                      ariaLabel='Youtube'
                    >
                      <img
                        src='/images/youtube-logo.svg'
                        alt='Youtube Logo'
                        className='h-5 w-5 rounded-full'
                      />
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className='w-full md:w-7/12 xl:w-[820px]'>
            <div className='grid gap-4 px-5 py-8 sm:grid-cols-2 sm:p-10 md:px-[50px] md:py-10 lg:grid-cols-3 xl:gap-12'>
              <div>
                <Heading
                  title='quick links'
                  isWhite
                  titleClassName='font-[700]'
                />
                <ul>
                  {navigationLinks.map((item, index) => (
                    <li
                      key={index}
                      className='group mb-2'
                    >
                      <NavLink
                        {...item}
                        className='squiggly-link line whitespace-nowrap text-base font-semibold leading-5 text-white after:text-white'
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
              {businessHours && (
                <div>
                  <Heading
                    title='hours'
                    isWhite
                    titleClassName='font-[700]'
                  />
                  <RichText
                    value={businessHours}
                    className='list-mb-2 text-base font-normal leading-[20px] text-white'
                  />
                </div>
              )}

              {locationInfo && (
                <div>
                  <Heading
                    title='location'
                    isWhite
                    titleClassName='font-[700]'
                  />
                  <RichText
                    value={locationInfo}
                    className='text-base font-normal leading-[20px] text-white'
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='absolute left-0 top-0 z-0 flex h-full w-full flex-col md:flex-row'>
          <div className='w-full bg-green-500 md:w-5/12 xl:w-1/3'></div>
          <div className='md:bg-element-half-circles-right w-full bg-blue-800 bg-[266px] bg-right bg-no-repeat md:w-7/12 xl:w-2/3 '></div>
        </div>
      </div>
      <div className='bg-blue-950'>
        <div className='container px-5 py-6 lg:py-10'>
          <div className='text-center'>
            <LogoLink
              className='mx-auto mb-3 md:h-[30px]'
              variant='light'
              accent='text-yellow-500'
            />
            <div className='text-center text-base font-light leading-5 text-white'>
              <p>{copyrightText}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
